import React, { useCallback, useContext, useEffect, useState } from 'react';
import type { PlansSliceProps } from '@ww-digital/web-palette-react/dist/components/Slice/PlansSlice/PlansSlice';
import type { QueryStringContextType } from '../../../context/querystring.context';
import type { OfferBundle } from '../../../hooks/useProducts';

import { PlansSlice } from '@ww-digital/web-palette-react/dist/components/Slice/PlansSlice/PlansSlice';
import { QueryStringContext } from '../../../context/querystring.context.ts';
import { useProducts } from '../../../hooks/useProducts.ts';
import { usePostalCodeAndLocation } from '../../../hooks/usePostalCodeAndLocation.ts';
import {
  getFeaturesList,
  getMappedPlan,
  getRecommendedIndexByPlanType,
} from './PlanSliceContainerUtil.ts';
import { useZipOwner } from '../../../hooks/useZipOwner.ts';
import {
  EntitlementContext,
  EntitlementContextType,
} from '../../../context/entitlement.context.ts';

type PlansDurationPlansDataType = {
  subscriptionType: string;
  offerBundleId: string;
};

// Temporary props added for current XS test(s)
type PlansDurationSliceXSProps = {
  xsExperimentTextId?: string;
  sliceData?: PlansSliceProps;
};

export type PlansSliceExtendedProps = PlansSliceProps &
  PlansDurationSliceXSProps & {
    plansData: PlansDurationPlansDataType[];
    defaultItemName: string;
    franchiseUrl: string;
    redirectOnFranchise?: boolean;
    headerText: string;
    subheaderText: string;
    featuresMinHeight: string;
    cardMinHeight: string;
    clinicData: any;
    [key: string]: any;
  };

interface PlansSliceContainerProps {
  daCategory: string;
  slice: PlansSliceExtendedProps;
}

export const PlansSliceContainer = ({
  slice,
}: PlansSliceContainerProps): JSX.Element => {
  const queryArgs = useContext<QueryStringContextType>(QueryStringContext);
  const [plansData, setPlansData] = useState<PlansSliceProps>({});
  const [currentOwnerId, setCurrentOwnerId] = useState(queryArgs.own || '37');
  const [currentPostalCode, setCurrentPostalCode] = useState('');

  const { entitlement } =
    useContext<EntitlementContextType>(EntitlementContext);

  const {
    loading: locationLoading,
    postalCode,
    updateUserPostalCode,
  } = usePostalCodeAndLocation();

  useEffect(() => {
    if (postalCode && !locationLoading) {
      setCurrentPostalCode(postalCode);
    }
  }, [postalCode, locationLoading]);

  const { ownerId, loading: zipOwnerLoading } = useZipOwner(postalCode);

  useEffect(() => {
    if (!zipOwnerLoading && ownerId && !queryArgs.own) {
      setCurrentOwnerId(ownerId);
    }
  }, [zipOwnerLoading, ownerId]);

  const offerBundleInputs = queryArgs.ob
    ? [{ offerBundleId: queryArgs.ob }]
    : slice.plansData.map((item: PlansDurationPlansDataType) => ({
        offerBundleId: item.offerBundleId || undefined,
        subscriptionType: item.offerBundleId
          ? undefined
          : item.subscriptionType,
      }));

  const stg = !!queryArgs.sid && !!queryArgs.vid && queryArgs.ppm === 'stg';
  const { offerBundles: offerBundlesData, loading: offerBundlesLoading } =
    useProducts(offerBundleInputs, currentOwnerId, stg);

  const recommendedIndex = getRecommendedIndexByPlanType(queryArgs.recommended);

  const initializeBundles = useCallback(() => {
    if (offerBundlesLoading || !offerBundlesData?.length) {
      return;
    }

    const offerBundles = offerBundlesData;

    const plans = offerBundles?.map(
      (offerBundle: OfferBundle, index: number) => {
        const plan = getMappedPlan(offerBundle, currentOwnerId, entitlement);
        plan.items = getFeaturesList(offerBundle);
        plan.checkListMinHeight = slice.featuresMinHeight
          ? `${slice.featuresMinHeight}px`
          : '';
        plan.cardMinHeight = slice.cardMinHeight
          ? `${slice.cardMinHeight}px`
          : '';
        plan.currentZipCode =
          offerBundle.subscriptionType === 'PREMIUM'
            ? queryArgs.own
              ? ''
              : currentPostalCode
            : '';
        plan.onZipCodeChange = async (currentZipCode: string) => {
          setCurrentPostalCode(currentZipCode);
          await updateUserPostalCode({
            variables: { postalCode: currentZipCode },
          });
        };
        plan.hasRecomendedTag = index === recommendedIndex;
        const preLoadedData = slice[offerBundle.offerBundleId];

        return { ...plan, ...preLoadedData };
      },
    );

    plans.push({
      ...slice.clinicData,
      hasRecomendedTag: recommendedIndex === plans.length,
    });

    const mergedData: PlansSliceProps = {
      headerText: slice.headerText,
      subheaderText: slice.subheaderText,
      plans: plans,
      selectedOfferIndex: recommendedIndex,
    };

    setPlansData(mergedData);
  }, [
    offerBundlesLoading,
    offerBundlesData,
    locationLoading,
    currentOwnerId,
    currentPostalCode,
    postalCode,
    queryArgs.op,
    queryArgs.st,
    queryArgs.ma,
  ]);

  useEffect(() => {
    initializeBundles();
  }, [
    offerBundlesLoading,
    locationLoading,
    postalCode,
    currentPostalCode,
    currentOwnerId,
  ]);

  const plansDurationSlice = <PlansSlice {...plansData} />;

  return <div>{plansDurationSlice}</div>;
};
